import { defaultLocale, supportedLocales } from './locales'

/**
 * 从URL参数中获取语言设置
 * 支持的参数名: lang, locale, language
 * 示例: ?lang=en-US 或 ?locale=zh-CN
 * 支持hash路由: ?tenantTag=nineh&lang=zh-CN/#/login
 */
export const getLocaleFromURL = () => {
  // 从完整URL中提取参数(支持hash路由场景)
  const fullUrl = window.location.href
  const questionMarkIndex = fullUrl.indexOf('?')
  const hashIndex = fullUrl.indexOf('#')
  
  let searchString = ''
  if (questionMarkIndex !== -1) {
    if (hashIndex !== -1 && hashIndex > questionMarkIndex) {
      // 参数在hash之前: ?param=value#/route
      searchString = fullUrl.substring(questionMarkIndex, hashIndex)
    } else {
      // 只有参数或参数在hash之后
      searchString = fullUrl.substring(questionMarkIndex)
    }
  }
  
  const urlParams = new URLSearchParams(searchString)
  
  // 尝试多个可能的参数名
  const locale = urlParams.get('lang') || 
                urlParams.get('locale') || 
                urlParams.get('language')
  
  return locale
}

/**
 * 从浏览器获取语言设置
 */
export const getBrowserLocale = () => {
  const browserLang = navigator.language || navigator.userLanguage
  
  // 标准化语言代码
  if (browserLang.startsWith('zh')) {
    return 'zh-CN'
  }
  if (browserLang.startsWith('en')) {
    return 'en-US'
  }
  if (browserLang.startsWith('ja')) {
    return 'ja-JP'
  }
  
  return null
}

/**
 * 从localStorage获取语言设置
 */
export const getStoredLocale = () => {
  try {
    return localStorage.getItem('locale')
  } catch (error) {
    console.warn('Failed to read locale from localStorage:', error)
    return null
  }
}

/**
 * 保存语言设置到localStorage
 */
export const setStoredLocale = (locale) => {
  try {
    localStorage.setItem('locale', locale)
  } catch (error) {
    console.warn('Failed to save locale to localStorage:', error)
  }
}

/**
 * 获取当前应该使用的语言
 * 优先级: URL参数 > localStorage > 浏览器语言 > 默认语言
 */
export const getCurrentLocale = () => {
  // 1. 优先从URL参数获取
  const urlLocale = getLocaleFromURL()
  const normalizedUrlLocale = normalizeLocale(urlLocale)
  if (normalizedUrlLocale) {
    setStoredLocale(normalizedUrlLocale) // 保存到localStorage
    return normalizedUrlLocale
  }
  
  // 2. 从localStorage获取
  const storedLocale = getStoredLocale()
  const normalizedStoredLocale = normalizeLocale(storedLocale)
  if (normalizedStoredLocale) {
    return normalizedStoredLocale
  }
  
  // 3. 从浏览器语言获取
  const browserLocale = getBrowserLocale()
  if (browserLocale && isValidLocale(browserLocale)) {
    return browserLocale
  }
  
  // 4. 返回默认语言
  return defaultLocale
}

/**
 * 切换语言
 * @param {string} locale - 语言代码，如 'zh-CN', 'en-US'
 */
export const changeLocale = (locale) => {
  setStoredLocale(locale)
  
  // 更新URL参数（支持hash路由）
  const fullUrl = window.location.href
  const questionMarkIndex = fullUrl.indexOf('?')
  const hashIndex = fullUrl.indexOf('#')
  
  let newUrl = fullUrl
  if (questionMarkIndex !== -1 && hashIndex !== -1 && hashIndex > questionMarkIndex) {
    // URL格式: ?param=value#/route
    const baseUrl = fullUrl.substring(0, questionMarkIndex)
    const searchString = fullUrl.substring(questionMarkIndex, hashIndex)
    const hashString = fullUrl.substring(hashIndex)
    
    const urlParams = new URLSearchParams(searchString)
    urlParams.set('lang', locale)
    newUrl = baseUrl + '?' + urlParams.toString() + hashString
  } else {
    // 使用标准URL API
    const url = new URL(fullUrl)
    url.searchParams.set('lang', locale)
    newUrl = url.toString()
  }
  
  window.history.replaceState({}, '', newUrl)
  
  // 重新加载页面以应用新语言
  window.location.reload()
}

/**
 * 检查语言代码是否有效
 */
export const isValidLocale = (locale) => {
  if (!locale) return false
  
  // 检查是否在支持的语言列表中
  return supportedLocales.some(supported => supported.code === locale)
}

/**
 * 标准化语言代码
 * 支持简短语言代码（如 en, zh, ja）映射到完整代码
 */
export const normalizeLocale = (locale) => {
  if (!locale) return null
  
  // 如果已经是完整的有效语言代码，直接返回
  if (isValidLocale(locale)) {
    return locale
  }
  
  // 简短代码映射
  const shortCodeMap = {
    'en': 'en-US',
    'zh': 'zh-CN',
    'ja': 'ja-JP',
  }
  
  const lowerLocale = locale.toLowerCase()
  
  // 直接匹配简短代码
  if (shortCodeMap[lowerLocale]) {
    return shortCodeMap[lowerLocale]
  }
  
  // 尝试匹配前缀（如 en-GB -> en-US）
  for (const [shortCode, fullCode] of Object.entries(shortCodeMap)) {
    if (lowerLocale.startsWith(shortCode)) {
      return fullCode
    }
  }
  
  return null
}
